import { Link } from "react-router-dom";
import { BookOpen, Quote, Users, Library, Trophy, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";

const HERO = "https://images.unsplash.com/photo-1722182877533-7378b60bf1e8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHwyfHxhbmNpZW50JTIwbGlicmFyeSUyMGJyaWdodHxlbnwwfHx8fDE3ODcxMzczMTB8MA&ixlib=rb-4.1.0&q=85";
const STATUE = "https://images.unsplash.com/photo-1571033690858-b336bfa1dd0f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MDV8MHwxfHNlYXJjaHwxfHxwaGlsb3NvcGh5JTIwc2N1bHB0dXJlfGVufDB8fHx8MTc4NzEzNzMxMHww&ixlib=rb-4.1.0&q=85";

const features = [
  { icon: Library, title: "Kitaplığını Takip Et", desc: "Okuduğun, okuduğun ve okuyacağın kitapları tek yerde yönet; yıllık okuma hedefini belirle." },
  { icon: Quote, title: "Alıntı ve İncelemeler", desc: "Beğendiğin pasajları paylaş, felsefi tartışmalara katıl, spoiler'ları gizle." },
  { icon: BookOpen, title: "Felsefi Kavram Dizini", desc: "Metinlerdeki felsefi terimlerin üzerine gel, anlamını anında öğren." },
  { icon: Users, title: "Aylık Okuma Kulübü", desc: "Her ay birlikte bir kitap oku, küratörlü tematik listelerle keşfet." },
  { icon: Trophy, title: "Lider Tabloları", desc: "En aktif okurlar, en çok okunan ve tartışılan kitaplar arasında yerini al." },
  { icon: BookOpen, title: "Kişisel Notlar", desc: "Sadece sana özel gizli çalışma notları tut, verilerini dilediğinde indir." },
];

export default function Landing() {
  return (
    <div className="min-h-screen bg-page">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-page/80 border-b border-line">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex items-center justify-between h-16">
          <div className="flex items-center gap-2">
            <BookOpen className="text-terra" size={24} />
            <span className="font-serif text-2xl text-ink tracking-tight">AktifOkur</span>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/giris"><Button variant="ghost" data-testid="landing-login" className="text-ink-2">Giriş Yap</Button></Link>
            <Link to="/kayit"><Button data-testid="landing-register" className="bg-terra hover:bg-terra-hover text-white rounded-full px-5">Üye Ol</Button></Link>
          </div>
        </div>
      </header>

      <section className="relative overflow-hidden grain">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 grid lg:grid-cols-2 gap-12 items-center py-16 lg:py-24">
          <div className="fade-up relative z-10">
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-terra mb-4">Aktiffelsefe Kitap Topluluğu</p>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-none text-ink">
              Bilgeliğin izinde,<br />birlikte okumak.
            </h1>
            <p className="text-lg text-ink-2 mt-6 max-w-xl leading-relaxed">
              Antik kütüphanelerin ruhunu modern bir toplulukla buluşturan kapalı devre okuma platformu.
              Kitaplarını takip et, felsefi alıntıları paylaş, birlikte düşün.
            </p>
            <div className="flex flex-wrap gap-3 mt-8">
              <Link to="/kayit"><Button data-testid="hero-cta" className="bg-terra hover:bg-terra-hover text-white rounded-full px-7 h-12 text-base">
                Topluluğa Katıl <ArrowRight size={18} className="ml-1" />
              </Button></Link>
              <Link to="/giris"><Button variant="outline" className="rounded-full px-7 h-12 text-base border-line text-ink-2 hover:bg-surface-alt">Zaten üyeyim</Button></Link>
            </div>
          </div>
          <div className="relative fade-up">
            <div className="aspect-[4/5] rounded-2xl overflow-hidden border border-line shadow-2xl">
              <img src={HERO} alt="Antik kütüphane" className="w-full h-full object-cover" />
            </div>
            <div className="absolute -bottom-6 -left-6 bg-surface border border-line rounded-xl p-4 shadow-xl max-w-[220px] hidden sm:block">
              <p className="font-serif text-lg italic text-ink leading-snug">"Kendini bil."</p>
              <p className="text-xs text-muted-foreground mt-1">— Delphi Tapınağı</p>
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16 lg:py-24">
        <div className="max-w-2xl">
          <h2 className="font-serif text-3xl sm:text-4xl tracking-tight text-ink">Okuma yolculuğun için her şey</h2>
          <p className="text-ink-2 mt-3">Düşünmeyi ve paylaşmayı teşvik eden araçlarla dolu bir topluluk.</p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-12">
          {features.map((f, i) => (
            <div key={i} className="border border-line rounded-xl p-6 bg-surface hover:shadow-md transition-shadow"
              style={{ animationDelay: `${i * 60}ms` }}>
              <div className="w-11 h-11 rounded-lg bg-terra/10 flex items-center justify-center mb-4">
                <f.icon className="text-terra" size={22} />
              </div>
              <h3 className="font-serif text-xl text-ink">{f.title}</h3>
              <p className="text-sm text-ink-2 mt-2 leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 pb-20">
          <div className="rounded-2xl overflow-hidden relative bg-ink">
            <img src={STATUE} alt="" className="absolute inset-0 w-full h-full object-cover opacity-30" />
            <div className="relative z-10 p-10 lg:p-16 text-center">
              <h2 className="font-serif text-3xl lg:text-5xl text-page tracking-tight">Düşünen okurların topluluğu</h2>
              <p className="text-page/80 mt-4 max-w-xl mx-auto">Üyelik admin onayı ile aktifleşir. Aramıza katıl, ilk kitabını rafına ekle.</p>
              <Link to="/kayit"><Button className="mt-8 bg-gold hover:bg-gold/90 text-ink rounded-full px-8 h-12 text-base font-medium">Hemen Başla</Button></Link>
            </div>
          </div>
        </div>
      </section>

      <footer className="border-t border-line py-8 text-center text-sm text-muted-foreground">
        <p>AktifOkur · Aktiffelsefe Kitap Topluluğu Portalı</p>
      </footer>
    </div>
  );
}
